import type { Tick } from '@/lib/ruler/compute-ruler-ticks'
import type { RulerTickViewportOpts } from './types/ruler-tick-viewport-opts'

function isImportantTick(t: Tick): boolean {
  if (t.label) return true
  if (t.level === 'major' || t.level === 'mid') return true
  if (t.isMajor) return true

  return false
}

/** Keep ticks whose painted position lies in the translated strip window visible in the ruler. */
export function filterTicksToStripViewport(
  ticks: Tick[],
  { scrollMain, viewportPx, rulerInset, overscanPx }: Omit<RulerTickViewportOpts, 'maxDom'>
): Tick[] {
  if (viewportPx < 2 || ticks.length === 0) return ticks

  const lo = scrollMain - overscanPx
  const hi = scrollMain + viewportPx + overscanPx

  return ticks.filter((t) => {
    const layoutPos = t.pos + rulerInset

    return layoutPos >= lo && layoutPos <= hi
  })
}

/**
 * If there are still too many ticks after viewport culling, drop minors on a stride while keeping
 * labeled / major / mid ticks when possible.
 */
export function capTicksDomBudget(ticks: Tick[], maxDom: number): Tick[] {
  if (ticks.length <= maxDom || maxDom < 1) return ticks

  const sorted = [...ticks].sort((a, b) => a.anchorU - b.anchorU)
  const important = sorted.filter(isImportantTick)
  const rest = sorted.filter((t) => !isImportantTick(t))

  if (important.length >= maxDom) {
    const stride = Math.ceil(sorted.length / maxDom)

    return sorted.filter((_, i) => i % stride === 0).slice(0, maxDom)
  }

  const budget = maxDom - important.length

  if (budget >= rest.length) {
    return sorted
  }

  if (budget <= 0) {
    return important.slice(0, maxDom)
  }

  const stride = Math.ceil(rest.length / budget)
  const sampled = rest.filter((_, idx) => idx % stride === 0)

  return [...important, ...sampled].sort((a, b) => a.anchorU - b.anchorU)
}

/** Viewport filter + DOM cap (Phase 2). */
export function viewportFilterAndCapTicks(ticks: Tick[], opts: RulerTickViewportOpts): Tick[] {
  const visible = filterTicksToStripViewport(ticks, opts)

  return capTicksDomBudget(visible, opts.maxDom)
}
